import { useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { useMemo, useCallback } from "react";
import { trpc } from "../trpc";
import type { FavoriteAsset, GetFavoriteAssetsOutput } from "@zapier/bff";
import { normalizeZapNodeId, AssetTypes } from "@zapier/utils";

// AssetRow uses convertToAssetId which adds a "type:" prefix (e.g. "zap:123", "table:uuid").
// We strip that prefix so favorites are stored and compared using raw IDs only.
// String() coercion is necessary because some asset IDs arrive as numbers at runtime.
const normalizeAssetId = (assetId: string | number, assetType: string) => {
  const id = String(assetId);
  const stripped = id.startsWith(`${assetType}:`)
    ? id.slice(assetType.length + 1)
    : id;
  return assetType === AssetTypes.Zap ? normalizeZapNodeId(stripped) : stripped;
};

// The BFF identifies a favorite by (asset_type, normalized_id), since IDs
// aren't unique across types (e.g. a canvas and a table can share an id).
// Every favorites lookup/comparison must key on both, not the id alone.
const getFavoriteKey = (assetId: string | number, assetType: string) =>
  `${assetType}:${normalizeAssetId(assetId, assetType)}`;

interface UseFavoriteAssetsOptions {
  enabled?: boolean;
}

export function useFavoriteAssets({
  enabled = true,
}: UseFavoriteAssetsOptions = {}) {
  const queryClient = useQueryClient();

  const favoriteAssetsKey = getQueryKey(
    trpc.userProperty.getFavoriteAssets,
    undefined,
    "query",
  );

  const query = trpc.userProperty.getFavoriteAssets.useQuery(undefined, {
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  });

  const mutation = trpc.userProperty.setFavoriteAssets.useMutation({
    onMutate: async (newData) => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: favoriteAssetsKey });

      // Snapshot previous value for rollback
      const previousData =
        queryClient.getQueryData<GetFavoriteAssetsOutput>(favoriteAssetsKey);

      // Optimistically update the cache
      queryClient.setQueryData<GetFavoriteAssetsOutput>(favoriteAssetsKey, {
        value: newData.value,
      });

      // Return context for rollback
      return { previousData };
    },
    onError: (_error, _newData, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(favoriteAssetsKey, context.previousData);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency with server
      queryClient.invalidateQueries({
        queryKey: favoriteAssetsKey,
        refetchType: "all",
      });
      // Invalidate hydrated favorites so the Favorites page refreshes on next visit
      const hydratedFavoriteAssetsKey = getQueryKey(
        trpc.userProperty.getHydratedFavoriteAssets,
        undefined,
        "any",
      );
      queryClient.invalidateQueries({
        queryKey: hydratedFavoriteAssetsKey,
      });
    },
  });

  // GET response has { value: [...] } structure
  const favoriteAssets = useMemo(
    () => query.data?.value || [],
    [query.data?.value],
  );

  const favoriteAssetIds = useMemo(() => {
    return new Set(
      favoriteAssets.map((item) =>
        getFavoriteKey(item.asset_id, item.asset_type),
      ),
    );
  }, [favoriteAssets]);

  const isFavorite = useCallback(
    (assetId: string, assetType: string) => {
      return favoriteAssetIds.has(getFavoriteKey(assetId, assetType));
    },
    [favoriteAssetIds],
  );

  const toggleFavorite = useCallback(
    (assetId: string, assetType: string) => {
      const key = getFavoriteKey(assetId, assetType);
      const currentFavorites = favoriteAssets;

      const isCurrentlyFavorite = currentFavorites.some(
        (item) => getFavoriteKey(item.asset_id, item.asset_type) === key,
      );

      const newFavorites: FavoriteAsset[] = isCurrentlyFavorite
        ? currentFavorites.filter(
            (item) => getFavoriteKey(item.asset_id, item.asset_type) !== key,
          )
        : [
            ...currentFavorites,
            {
              asset_id: normalizeAssetId(assetId, assetType),
              asset_type: assetType,
            },
          ];

      mutation.mutate({ value: newFavorites });
    },
    [favoriteAssets, mutation],
  );

  // Remove from favorites (used when deleting assets). Cache invalidation is
  // handled by the mutation's onSettled handler above.
  const removeFromFavorites = useCallback(
    (assetId: string, assetType: string) => {
      const key = getFavoriteKey(assetId, assetType);

      if (!isFavorite(assetId, assetType)) {
        return;
      }

      const newFavorites = favoriteAssets.filter(
        (item) => getFavoriteKey(item.asset_id, item.asset_type) !== key,
      );

      mutation.mutate({ value: newFavorites });
    },
    [favoriteAssets, isFavorite, mutation],
  );

  // Remove several assets from favorites in a single write. The favorites
  // property is replaced wholesale on every write, so removing multiple
  // assets via sequential removeFromFavorites calls would race: each call
  // reads the same pre-mutation snapshot and its write can silently restore
  // memberships removed by an earlier (now-overwritten) call.
  const removeManyFromFavorites = useCallback(
    async (assets: { assetId: string; assetType: string }[]) => {
      if (assets.length === 0) {
        return;
      }

      // query.data is undefined before the first successful load (or after a
      // failed one), in which case favoriteAssets defaults to []. Diffing
      // against that would look like a legitimate no-op and silently skip
      // cleanup, so fetch the real favorites first instead of trusting it.
      const currentFavorites =
        query.data?.value ?? (await query.refetch()).data?.value;

      if (currentFavorites === undefined) {
        // Both the initial load and the recovery refetch failed, so we have
        // no ground truth for the current favorites. Diffing against [] here
        // would either wrongly skip cleanup (if favorites aren't actually
        // empty) or risk overwriting real favorites we never saw. Bail
        // loudly instead of guessing.
        console.error(
          "removeManyFromFavorites: could not load current favorites, skipping cleanup",
          assets,
        );
        return;
      }

      const keysToRemove = new Set(
        assets.map(({ assetId, assetType }) =>
          getFavoriteKey(assetId, assetType),
        ),
      );

      const newFavorites = currentFavorites.filter(
        (item) =>
          !keysToRemove.has(getFavoriteKey(item.asset_id, item.asset_type)),
      );

      if (newFavorites.length === currentFavorites.length) {
        return;
      }

      mutation.mutate({ value: newFavorites });
    },
    [mutation, query],
  );

  return {
    favoriteAssets,
    favoriteAssetIds,
    isFavorite,
    isLoading: query.isLoading,
    isMutating: mutation.isPending,
    toggleFavorite,
    removeFromFavorites,
    removeManyFromFavorites,
    setFavoriteAssets: mutation.mutate,
  };
}
